import React from "react";
import { Box } from "@mui/material";
import PlaceOutlinedIcon from "@mui/icons-material/PlaceOutlined";
import CalendarTodayOutlinedIcon from "@mui/icons-material/CalendarTodayOutlined";
import LocalShippingOutlinedIcon from "@mui/icons-material/LocalShippingOutlined";
import StorefrontOutlinedIcon from "@mui/icons-material/StorefrontOutlined";
import ScheduleRoundedIcon from "@mui/icons-material/ScheduleRounded";
import StickyNote2OutlinedIcon from "@mui/icons-material/StickyNote2Outlined";
import MeetingRoomOutlinedIcon from "@mui/icons-material/MeetingRoomOutlined";
import PhoneOutlinedIcon from "@mui/icons-material/PhoneOutlined";
import { C, FOCUS_RING, FONT_BODY } from "../../../utils/designTokens";
import { CardTitle, DetailCard } from "./detailUi";
import { DROPOFF_LABELS, dayText, deliverySource, formatPhone, isInStoreOrder, isPickupOrder, orderTz, telHref, timeText, toDate } from "./orderProgress";

function Row({ icon: Icon, label, children, full }) {
  return (
    <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1.5, minWidth: 0, gridColumn: full ? "1 / -1" : "auto" }}>
      <Box aria-hidden="true" sx={{ width: 36, height: 36, flexShrink: 0, borderRadius: "10px", bgcolor: C.surface, color: C.ink, display: "flex", alignItems: "center", justifyContent: "center" }}>
        <Icon sx={{ fontSize: 19 }} />
      </Box>
      <Box sx={{ minWidth: 0 }}>
        <Box component="dt" sx={{ fontFamily: FONT_BODY, fontSize: 13, fontWeight: 500, color: C.muted }}>
          {label}
        </Box>
        <Box component="dd" sx={{ m: 0, mt: 0.25, fontFamily: FONT_BODY, fontSize: 15, fontWeight: 600, color: C.ink, lineHeight: 1.4, overflowWrap: "anywhere" }}>
          {children}
        </Box>
      </Box>
    </Box>
  );
}

const when = (v, tz) => {
  const d = toDate(v);
  return d ? `${dayText(d, tz)} · ${timeText(d, tz)}` : null;
};

/** Where it goes (or where to collect it), when, how, and the notes. Rows with nothing to say are left out. */
function DeliveryDetails({ order: o }) {
  const pickup = isPickupOrder(o);
  const addr = o.deliveryAddress || {};
  const unit = [addr.apartment && `Apt ${addr.apartment}`, addr.buildingName, addr.entryCode && `Code ${addr.entryCode}`].filter(Boolean).join(" · ");
  const placed = when(o.createdAt, orderTz(o));
  const created = toDate(o.createdAt);
  const forDate = toDate(o.orderDate);
  // Scheduled: the order is for a time well after it was placed.
  const scheduled = o.orderStatus === "SCHEDULED" || (created && forDate && forDate - created > 20 * 60000);
  const restaurantTel = telHref(o.restaurant?.phone);
  const inStore = isInStoreOrder(o);
  const type = inStore ? "In-store order" : pickup ? "Pickup" : deliverySource(o) === "RESTAURANT" ? "Restaurant delivery" : "RushMyFood Delivery";

  return (
    <DetailCard aria-labelledby="od-details-title">
      <CardTitle id="od-details-title">{inStore ? "Order details" : pickup ? "Pickup details" : "Delivery details"}</CardTitle>
      <Box component="dl" sx={{ m: 0, mt: 2, display: "grid", gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" }, gap: "18px 20px" }}>
        {pickup
          ? (o.restaurant?.name || o.restaurant?.address) && (
              <Row icon={StorefrontOutlinedIcon} label={inStore ? "Bought at" : "Pick up from"} full>
                {o.restaurant?.name}
                {o.restaurant?.address && (
                  <Box component="span" sx={{ display: "block", fontWeight: 500, color: C.muted, fontSize: 14 }}>
                    {o.restaurant.address}
                  </Box>
                )}
              </Row>
            )
          : addr.deliveryAddress && (
              <Row icon={PlaceOutlinedIcon} label="Deliver to" full>
                {addr.deliveryAddress}
                {(unit || addr.details) && (
                  <Box component="span" sx={{ display: "block", fontWeight: 500, color: C.muted, fontSize: 14 }}>
                    {[unit, addr.details].filter(Boolean).join(" · ")}
                  </Box>
                )}
              </Row>
            )}
        {placed && (
          <Row icon={CalendarTodayOutlinedIcon} label="Order placed">
            {placed}
          </Row>
        )}
        <Row icon={pickup ? StorefrontOutlinedIcon : LocalShippingOutlinedIcon} label={pickup ? "Order type" : "Delivery type"}>
          {type}
        </Row>
        {scheduled && forDate && (
          <Row icon={ScheduleRoundedIcon} label={pickup ? "Pickup scheduled for" : "Delivery scheduled for"}>
            {when(forDate, orderTz(o))}
          </Row>
        )}
        {!pickup && DROPOFF_LABELS[addr.dropoffOption] && (
          <Row icon={MeetingRoomOutlinedIcon} label="Drop-off">
            {DROPOFF_LABELS[addr.dropoffOption]}
          </Row>
        )}
        {pickup && restaurantTel && (
          <Row icon={PhoneOutlinedIcon} label="Restaurant phone">
            <Box component="a" href={restaurantTel} sx={{ ...FOCUS_RING, borderRadius: "6px", color: C.redLink, textDecoration: "none", "&:hover": { textDecoration: "underline" } }}>
              {formatPhone(o.restaurant.phone)}
            </Box>
          </Row>
        )}
        {o.instructions && (
          <Row icon={StickyNote2OutlinedIcon} label={pickup ? "Instructions" : "Driver instructions"} full>
            {o.instructions}
          </Row>
        )}
      </Box>
    </DetailCard>
  );
}

export default DeliveryDetails;
